import moment from 'moment';
import { DateRange, DayPicker } from 'react-day-picker';
import { FC, useEffect, useRef } from 'react';
import { DateSelectorProps, Mode } from './datePicker.type';
import './datePicker.css';
import left from '../../assets/left-arrow.png';
import right from '../../assets/right-arrow.png';
import {
  customLocale,
  getFirstAndLastDayOfMonth,
  getIdAttributes,
} from '../../utils/helper';
import { Trans } from 'react-i18next';

export const idPrefix: string = 'date-selector';

const DateSelector: FC<DateSelectorProps> = (props) => {
  const dateSelectorRef = useRef<HTMLDivElement>(null);
  const isSingleMode: boolean = props.mode === Mode.Single;
  const prefix: string = idPrefix;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dateSelectorRef.current &&
        !dateSelectorRef.current.contains(event.target as Node)
      ) {
        props.setShowDatePicker(false);
      }
    };

    if (props.showDatePicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [props.showDatePicker]);

  const handleApplyClick = () => {
    if (props.selectedDate) {
      props.handleApply(props.selectedDate);
    }
    props.setShowDatePicker(false);
  };

  const handleMonthChange = (newMonth: Date) => {
    props.setMonth(newMonth);
  };

  const goToNextMonth = () => {
    const current = props.month ?? new Date();
    const nextMonth = new Date(current.getFullYear(), current.getMonth() + 1);
    if (nextMonth <= props.maxDate.toDate()) {
      handleMonthChange(nextMonth);
    }
  };

  const goToPreviousMonth = () => {
    const current: Date = props.month ?? new Date();
    const prevMonth: Date = new Date(
      current.getFullYear(),
      current.getMonth() - 1,
    );
    if (prevMonth >= props.minDate.toDate()) {
      handleMonthChange(prevMonth);
    }
  };

  const handleWholeMonth = () => {
    if (props.wholeMonth) {
      props.setSelectedDate({ from: props.selectedDate?.from, to: undefined });
      props.setWholeMonth(false);
    } else {
      props.setSelectedDate(
        getFirstAndLastDayOfMonth(
          props.month ?? props.selectedDate?.from ?? props.maxDate.toDate(),
        ),
      );
      props.setWholeMonth(true);
    }
  };

  return (
    <div
      className="flex items-center justify-center"
      {...getIdAttributes(prefix, 'overlay')}
    >
      <div
        className={`date-modal ${props.className ?? ''} ${props.position === 'left' ? 'left-0' : props.position === 'right' ? 'right-0' : ''}`}
        ref={dateSelectorRef}
        {...getIdAttributes(prefix, 'container')}
      >
        <div className="flex justify-center">
          <div className="flex justify-between w-[15.625em] mt-[1.3125em] text-[black] text-[0.875em] font-semibold">
            <div
              className="cursor-pointer"
              onClick={goToPreviousMonth}
              {...getIdAttributes(prefix, 'previous-month')}
            >
              <img src={left} height={10} width={10} alt="Previous" />
            </div>
            <span {...getIdAttributes(prefix, 'month-year')}>
              {moment(props.month).format('MMMM YYYY')}
            </span>
            <div
              {...getIdAttributes(prefix, 'next-month')}
              className="cursor-pointer"
              onClick={goToNextMonth}
            >
              <img src={right} height={10} width={10} alt="Next" />
            </div>
          </div>
        </div>

        <div className="flex justify-between gap-5 mt-3.5 px-8">
          <div className="flex items-center gap-[0.6875em] flex-grow text-[#707070] text-[0.75em] py-[0.1875em] px-[0.3125em]">
            <div>From</div>
            <div
              {...getIdAttributes(prefix, 'from-date')}
              className="border border-[#EBEBEB] rounded-[0.9375em] bg-white h-6 flex items-center justify-center w-[7.5em] text-[#2C2C2C] font-semibold"
            >
              {props.selectedDate?.from
                ? moment(props.selectedDate.from).format('DD/MM/YYYY')
                : ''}
            </div>
          </div>

          {!isSingleMode && (
            <div className="flex items-center gap-[0.6875em] flex-grow text-[#707070] text-[0.75em] py-[0.1875em] px-[0.3125em]">
              <div>To</div>
              <div
                {...getIdAttributes(prefix, 'to-date')}
                className="border border-[#EBEBEB] rounded-[0.9375em] bg-white h-6 flex items-center justify-center w-[7.5em] text-[#2C2C2C] font-semibold"
              >
                {props.selectedDate?.to
                  ? moment(props.selectedDate.to).format('DD/MM/YYYY')
                  : ''}
              </div>
            </div>
          )}
        </div>

        <div
          className="flex justify-center"
          {...getIdAttributes(prefix, 'calendar-container')}
        >
          {(() => {
            const dayPickerProps = {
              defaultMonth: props.maxDate.toDate(),
              month: props.month ?? props.selectedDate?.from,
              onMonthChange: handleMonthChange,
              disabled: {
                after: props.maxDate.toDate(),
                before: props.minDate.toDate(),
              },
              locale: customLocale,
            };

            return props.mode === Mode.Single ? (
              <DayPicker
                mode="single"
                selected={props.selectedDate?.from}
                onSelect={(date: Date | undefined) => {
                  props.setSelectedDate(
                    date ? { from: date, to: date } : undefined,
                  );
                }}
                {...dayPickerProps}
                {...getIdAttributes(prefix, 'single-calendar')}
              />
            ) : (
              <DayPicker
                mode="range"
                selected={props.selectedDate}
                onSelect={(range: DateRange | undefined) => {
                  props.setSelectedDate(range);
                }}
                {...dayPickerProps}
                {...getIdAttributes(prefix, 'range-calendar')}
              />
            );
          })()}
        </div>

        <div className="flex justify-between border-t border-[#F4F4F4] mb-4 pt-3 px-2">
          {!isSingleMode && (
            <label
              {...getIdAttributes(prefix, 'whole-month-label')}
              className="text-THEME_COLOR text-[0.6875em] flex items-center p-2 gap-[0.8em] cursor-pointer font-semibold"
            >
              <input
                type="checkbox"
                name="wholeMonth"
                className="cursor-pointer"
                checked={props.wholeMonth}
                onChange={handleWholeMonth}
                {...getIdAttributes(prefix, 'whole-month-checkbox')}
              />
              <Trans i18nKey="wholeMonth" />
            </label>
          )}

          <div className="flex gap-2 ml-auto">
            <button
              className="cancel-button"
              onClick={props.handleCancel}
              {...getIdAttributes(prefix, 'cancel-button')}
            >
              <Trans i18nKey="userManagement.cancel" />
            </button>
            <button
              {...getIdAttributes(prefix, 'apply-button')}
              className="apply-button"
              onClick={handleApplyClick}
            >
              <Trans i18nKey="userManagement.userFilter.apply" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DateSelector;
